import { useTranslationStore } from '@src/stores-v2/StoreContext';
import { useMemo } from 'react';

import type { TranscriptElementWithClipSegmentAttributes } from '@src/types.ts/transcript';
import type { TranslationPhrase } from '@src/types.ts/translation';

export interface TranslatedElement {
  /** The original element, unmodified (reference equality preserved). */
  element: TranscriptElementWithClipSegmentAttributes;

  /** What to render for this element's visible text. */
  displayValue: string;

  /** True if this element is the first in its phrase's originalIndices. */
  isFirstInPhrase: boolean;

  /**
   * The phrase this element belongs to, if a translation is loaded AND a
   * phrase covers this element. Null otherwise.
   */
  phrase: TranslationPhrase | null;

  /** True when displayValue reflects a user edit (pending or saved). */
  isUserEdited: boolean;

  /**
   * Earliest `ts` among the phrase's text elements, when available. Used
   * for the playhead highlight: a translated phrase renders on its first
   * element only, so the highlight needs the full phrase's time span
   * (`phraseStartTs` → `phraseEndTs`) rather than the individual element's
   * timestamps. Null when no translation is loaded or none of the phrase's
   * text elements carry timestamps.
   */
  phraseStartTs: number | null;

  /** Latest `end_ts` among the phrase's text elements. See `phraseStartTs`. */
  phraseEndTs: number | null;

  /**
   * `initial_index` of the first timed text element in this phrase. Used as
   * the seek target for click-to-play: the phrase-leading element may be a
   * whitespace/punct without a timestamp, so clicking it and seeking by its
   * own index silently no-ops. Null when the phrase has no timed text
   * members or no translation is loaded.
   */
  phraseFirstTextIndex: number | null;
}

export interface UseTranslatedElementsOptions {
  /**
   * When true, hide elements whose state is `cut` or `removed` so only
   * in-scope content renders. Applied AFTER phrase lookup so phrases
   * with mixed cut/kept members still render via their surviving
   * members (the lead role is promoted to the first surviving member
   * when the original lead index sits in the cut region).
   *
   * Pass this for Creator Studio's translated transcript view (T45).
   * Leave `false` (default) for redline views (T46) that surface
   * cut/removed state visually.
   *
   * No-op for Content Studio's T&S tab, which passes the original
   * transcription — no cut/removed state exists on that array.
   */
  hideOutOfScopeElements?: boolean;
}

/**
 * Pure mapping from (baseElements, language) to a render-ready array where
 * text elements' visible text is replaced with the translated phrase value.
 *
 * Does NOT mutate `baseElements`. The returned objects wrap the original
 * elements by reference — metadata (ts, speaker_label, state, etc.) is read
 * from the original via `element`. Never persists a parallel structure.
 */
export function useTranslatedElements(
  baseElements: TranscriptElementWithClipSegmentAttributes[],
  language: string,
  options: UseTranslatedElementsOptions = {},
): TranslatedElement[] {
  const translationStore = useTranslationStore();
  const translation = translationStore.getTranslation(language);
  const { hideOutOfScopeElements = false } = options;

  // Build initial_index → phrase + phrase → firstIndex + phrase → time
  // range lookups once per translation/baseElements change so per-element
  // mapping stays constant-time.
  //
  // The splitter peels boundary whitespace into bare-whitespace phrases
  // (PLAT-9618), so multi-index content phrases always start on a
  // non-whitespace token. Claim every member of `originalIndices` for
  // the phrase and use `originalIndices[0]` as the lead — interior
  // whitespace inside a multi-word phrase still gets claimed (rendered
  // as a `phrase-continuation`, display:none) and bare-whitespace
  // phrases naturally lead on their single whitespace index.
  //
  // `baseElements` is expected to be free of synthetic whitespaces
  // (`initial_index < 0` from `fillInWhitespacesInTranscriptElements`):
  // `Transcript.tsx` filters them out before calling this hook, since
  // synthetics duplicate the bare-whitespace phrases' job and would
  // render unphrased as visible extra spaces.
  const {
    phraseByInitialIndex,
    phraseByUuid,
    firstIndexByPhrase,
    phraseTimeRangeByPhrase,
    firstTextIndexByPhrase,
  } = useMemo(() => {
    const byIndex = new Map<number, TranslationPhrase>();
    const byUuid = new Map<string, TranslationPhrase>();
    const firstByPhrase = new Map<TranslationPhrase, number>();
    const timeRangeByPhrase = new Map<
      TranslationPhrase,
      { startTs: number | null; endTs: number | null }
    >();
    const firstTextByPhrase = new Map<TranslationPhrase, number>();
    if (!translation) {
      return {
        phraseByInitialIndex: byIndex,
        phraseByUuid: byUuid,
        firstIndexByPhrase: firstByPhrase,
        phraseTimeRangeByPhrase: timeRangeByPhrase,
        firstTextIndexByPhrase: firstTextByPhrase,
      };
    }
    const elementByIndex = new Map<
      number,
      TranscriptElementWithClipSegmentAttributes
    >();
    for (const el of baseElements) elementByIndex.set(el.initial_index, el);

    for (const phrase of translation.phrases) {
      if (phrase.originalIndices.length === 0) continue;
      // PLAT-10611 — register the phrase under each of its stable element
      // UUIDs (content-only) so the live join can match by `id`/`source_uuid`
      // before the positional integer. `originalIndices` still fills `byIndex`
      // below as the transition-safe fallback and drives ordering/lead/timing.
      if (phrase.originalElementIds && phrase.originalElementIds.length > 0) {
        for (const id of phrase.originalElementIds) byUuid.set(id, phrase);
      }
      let startTs: number | null = null;
      let endTs: number | null = null;
      let firstTextIndex: number | null = null;
      for (const idx of phrase.originalIndices) {
        byIndex.set(idx, phrase);
        const el = elementByIndex.get(idx);
        if (el && el.type === 'text') {
          if (typeof el.ts === 'number') {
            if (startTs === null || el.ts < startTs) startTs = el.ts;
            if (firstTextIndex === null || idx < firstTextIndex)
              firstTextIndex = idx;
          }
          if (typeof el.end_ts === 'number') {
            if (endTs === null || el.end_ts > endTs) endTs = el.end_ts;
          }
        }
      }
      firstByPhrase.set(phrase, Math.min(...phrase.originalIndices));
      timeRangeByPhrase.set(phrase, { startTs, endTs });
      if (firstTextIndex !== null)
        firstTextByPhrase.set(phrase, firstTextIndex);
    }
    return {
      phraseByInitialIndex: byIndex,
      phraseByUuid: byUuid,
      firstIndexByPhrase: firstByPhrase,
      phraseTimeRangeByPhrase: timeRangeByPhrase,
      firstTextIndexByPhrase: firstTextByPhrase,
    };
  }, [translation, baseElements]);

  // NOT memoized — MobX reactivity tracks store reads inside the parent
  // observer. A pending-edit mutation must invalidate consumers on every
  // re-render; a stale useMemo would hide the new value.
  const mapped = baseElements.map(element =>
    mapElement(
      element,
      phraseByInitialIndex,
      phraseByUuid,
      firstIndexByPhrase,
      phraseTimeRangeByPhrase,
      firstTextIndexByPhrase,
      translationStore,
      language,
    ),
  );

  // Determine which phrases need their lead promoted to the first KEPT
  // member. A phrase needs promotion when its natural lead (lowest
  // `originalIndices`) is cut/removed AND some member survives. Without
  // promotion, the phrase's translated text renders at the cut-lead
  // position (redline: mixed into cut content; clip mode: filtered out
  // entirely), so partial phrases that should appear visibly alongside
  // kept content get lost.
  //
  // Applied in BOTH modes so the set of visible phrases in redline ==
  // set of visible phrases in clip mode: partial phrases always appear
  // at their first-kept position. Fully-cut phrases (no kept members)
  // are skipped here and retain their natural lead — in clip mode they
  // get filtered; in redline they render struck at the lead's original
  // position.
  const phrasesWithKeptLead = new Set<TranslationPhrase>();
  const phrasesWithAnyKept = new Set<TranslationPhrase>();
  for (const t of mapped) {
    if (!t.phrase) continue;
    const isKept =
      t.element.state !== 'cut' && t.element.state !== 'removed';
    if (!isKept) continue;
    phrasesWithAnyKept.add(t.phrase);
    if (t.isFirstInPhrase) phrasesWithKeptLead.add(t.phrase);
  }
  const phrasesNeedingPromotion = new Set<TranslationPhrase>();
  for (const phrase of phrasesWithAnyKept) {
    if (!phrasesWithKeptLead.has(phrase)) {
      phrasesNeedingPromotion.add(phrase);
    }
  }

  const applyPromotion = (
    elements: TranslatedElement[],
  ): TranslatedElement[] => {
    if (phrasesNeedingPromotion.size === 0) return elements;
    const promoted = new Set<TranslationPhrase>();
    return elements.map(t => {
      if (!t.phrase || !phrasesNeedingPromotion.has(t.phrase)) return t;
      const isKept =
        t.element.state !== 'cut' && t.element.state !== 'removed';
      // Demote the natural (cut) lead to a continuation so its strike
      // styling doesn't render the phrase's translated text in the
      // cut region; the kept member below takes over as the visible
      // lead. Only applies when the natural lead IS cut/removed —
      // otherwise there's no reason to demote.
      if (t.isFirstInPhrase && !isKept) {
        return { ...t, isFirstInPhrase: false, displayValue: '' };
      }
      if (isKept && !promoted.has(t.phrase)) {
        promoted.add(t.phrase);
        // PLAT-10611 (F4) — id-only identity read (see `mapElement`).
        const translatedValue =
          translationStore.getTranslatedPhraseByElementIds(
            language,
            t.phrase.originalElementIds,
          ) ?? t.phrase.translatedValue;
        const hasPending = translationStore.hasPendingEditByElementIds(
          language,
          t.phrase.originalElementIds,
        );
        return {
          ...t,
          isFirstInPhrase: true,
          displayValue: translatedValue,
          isUserEdited: hasPending || t.phrase.isUserEdited,
          // After promotion the phrase renders at THIS element's
          // position, not at its original first-text member. Override
          // `phraseFirstTextIndex` so paragraph/speaker break detection
          // in `buildRenderItems` and click-to-seek both track the
          // visible rendering location. Without this, partial phrases
          // that cross a clip segment boundary inherit the cut region's
          // paragraph_id and get merged into the wrong paragraph — the
          // rendered content of segment 2/3 then disappears into the
          // paragraph of segment 1.
          phraseFirstTextIndex:
            t.element.type === 'text'
              ? t.element.initial_index
              : t.phraseFirstTextIndex,
        };
      }
      return t;
    });
  };

  if (!hideOutOfScopeElements) return applyPromotion(mapped);

  // Clip-mode filter: drop cut/removed elements, then promote as above.
  const kept = mapped.filter(
    t => t.element.state !== 'cut' && t.element.state !== 'removed',
  );
  return applyPromotion(kept);
}

function mapElement(
  element: TranscriptElementWithClipSegmentAttributes,
  phraseByInitialIndex: Map<number, TranslationPhrase>,
  phraseByUuid: Map<string, TranslationPhrase>,
  firstIndexByPhrase: Map<TranslationPhrase, number>,
  phraseTimeRangeByPhrase: Map<
    TranslationPhrase,
    { startTs: number | null; endTs: number | null }
  >,
  firstTextIndexByPhrase: Map<TranslationPhrase, number>,
  translationStore: ReturnType<typeof useTranslationStore>,
  language: string,
): TranslatedElement {
  // Phrase membership is looked up BEFORE the text/punct split. A phrase
  // claims a contiguous span of source indices (words + punct + whitespace);
  // every member of that span renders as part of the phrase — the first
  // element holds the translated string, all others render empty. Only
  // elements that don't belong to any phrase fall through to the
  // per-element pass-through below. Previously we short-circuited on
  // `!isTextElement(element)` before checking phrases, which caused the
  // punct (".") and whitespace (" ") members of a phrase to render their
  // raw source values, producing stray "." tokens and extra blank rows
  // between sentences in the translated view.
  // PLAT-10611 — membership is UUID-first, integer-fallback. Match by the
  // element's stable id (`id ?? source_uuid`) so a clip element whose
  // `initial_index` has drifted from story-index space still joins its real
  // phrase; fall back to the positional integer for legacy blobs whose phrases
  // carry no `originalElementIds` (and for content-less whitespace members,
  // which have no id). Ordering/lead/timing/identity stay on the integer.
  const uuidKey = element.id ?? element.source_uuid;
  const phrase =
    (uuidKey != null ? phraseByUuid.get(uuidKey) : undefined) ??
    phraseByInitialIndex.get(element.initial_index) ??
    null;

  if (phrase) {
    const firstIndex =
      firstIndexByPhrase.get(phrase) ?? phrase.originalIndices[0];
    const isFirstInPhrase = element.initial_index === firstIndex;
    const timeRange = phraseTimeRangeByPhrase.get(phrase) ?? {
      startTs: null,
      endTs: null,
    };
    const phraseFirstTextIndex = firstTextIndexByPhrase.get(phrase) ?? null;

    if (!isFirstInPhrase) {
      return {
        element,
        displayValue: '',
        isFirstInPhrase: false,
        phrase,
        isUserEdited: false,
        phraseStartTs: timeRange.startTs,
        phraseEndTs: timeRange.endTs,
        phraseFirstTextIndex,
      };
    }

    // PLAT-10611 (F4) — resolve the pending-edit / current value by the phrase's
    // STABLE element ids (id-only identity), not the positional integer, so a
    // drifted clip reads the right edit. Falls back to the phrase's own
    // `translatedValue` / `isUserEdited` (which the optimistic fold keeps
    // current) for id-less legacy phrases — dropped on the id path, never
    // integer-matched. (Ordering/lead/timing/membership stay as F3 set them.)
    const translatedValue =
      translationStore.getTranslatedPhraseByElementIds(
        language,
        phrase.originalElementIds,
      ) ?? phrase.translatedValue;
    const hasPending = translationStore.hasPendingEditByElementIds(
      language,
      phrase.originalElementIds,
    );

    return {
      element,
      displayValue: translatedValue,
      isFirstInPhrase: true,
      phrase,
      isUserEdited: hasPending || phrase.isUserEdited,
      phraseStartTs: timeRange.startTs,
      phraseEndTs: timeRange.endTs,
      phraseFirstTextIndex,
    };
  }

  // Unphrased element (no translation loaded, or an index outside every
  // phrase's range). Preserve the source value so punctuation and any
  // residual whitespace render verbatim, exactly like `TranscriptionText`
  // does for the source view.
  return {
    element,
    displayValue: element.value ?? '',
    isFirstInPhrase: true,
    phrase: null,
    isUserEdited: false,
    phraseStartTs: null,
    phraseEndTs: null,
    phraseFirstTextIndex: null,
  };
}
